// eslint-disable-next-line jira/filename-case
import React, { Component, type ReactNode } from 'react';
import get from 'lodash/get';
import { ApolloProvider } from '@apollo/react-hooks';
import type { ApolloClient } from 'apollo-client';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import type { ApolloClientConfig, CacheShape, ApolloClientId } from './common/types.tsx';

let CLIENTS_CACHE: {
	[key: string]: ApolloClient<CacheShape>;
} = {};

autoBeforeEachTest(() => {
	CLIENTS_CACHE = {};
});

export const resetCache = (clientId: ApolloClientId): void => {
	// we need to `clearStore` directly to the InMemoryCache instance
	CLIENTS_CACHE[clientId]?.clearStore();
	delete CLIENTS_CACHE[clientId];
};

export const resetCacheAsync = async (clientId: ApolloClientId): Promise<void> => {
	// we need to `clearStore` directly to the InMemoryCache instance
	const client = CLIENTS_CACHE[clientId];
	delete CLIENTS_CACHE[clientId];
	await client?.clearStore();
};

/**
 * Serialise all Apollo caches to strings for rehydration
 */
export const getSerialisedCaches = (): {
	[key: string]: CacheShape;
} =>
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	Object.keys(CLIENTS_CACHE).reduce<Record<string, any>>(
		(acc, key) =>
			Object.assign(acc, {
				[`apollo-cache/${key}`]: CLIENTS_CACHE[key].extract(),
			}),
		{},
	);

type Props = {
	client: ApolloClientConfig;
	children: ReactNode;
};

export const getClient = ({
	clientId,
	clientCreator,
}: ApolloClientConfig): ApolloClient<CacheShape> => {
	let client = CLIENTS_CACHE[clientId];
	if (!client) {
		client = clientCreator();
		CLIENTS_CACHE[clientId] = client;
	}
	return client;
};

// oxlint-disable-next-line react/prefer-function-component
export class ApolloClientProvider extends Component<Props> {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	getClient(): ApolloClient<any> {
		return getClient(this.props.client);
	}

	render(): React.JSX.Element {
		const { children } = this.props;

		if (!get(this.props, 'client.clientId')) {
			throw new Error('Please provide a client configuration');
		}

		const client = this.getClient();

		return <ApolloProvider client={client}>{children}</ApolloProvider>;
	}
}
